import { computed, inject, Injectable, Signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { patchState, signalState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  AssetFiltersStore,
  AssetListFiltersOptions,
  EMPTY_FILTERING_OPTIONS,
  FilterSelection,
  toAssetFilterPredicate,
  toAssetListFilters,
  toCategoryOptions,
  toRoomOptions
} from '@simOn/asset/element/models';
import { isAutoDetectPreviewAsset } from '@simOn/asset/auto-detect-asset/models';
import { TAsset } from '@simOn/asset/modifying/models';
import { SpaceFacade } from '@simOn/space';
import { catchError, distinctUntilChanged, filter, Observable, of, pipe, switchMap, tap } from 'rxjs';
import { AssetsApiService } from './assets-api.service';

interface AssetWalkFiltersModel {
  filteringOptions: AssetListFiltersOptions;
  selectedRooms: string[];
  selectedCategories: string[];
  selectedCreators: string[];
  selectedLastEdit: number | undefined;
}

const INITIAL_STATE: AssetWalkFiltersModel = {
  filteringOptions: EMPTY_FILTERING_OPTIONS,
  selectedRooms: [],
  selectedCategories: [],
  selectedCreators: [],
  selectedLastEdit: undefined
};

/**
 * Asset filters scoped to the 3D walk. Backs the shared asset filters dialog
 * and exposes a client-side predicate applied to assets already loaded for the
 * walk (context panel list and scene markers). Provide it at the walk level so
 * the selection resets when the user leaves the walk.
 */
@Injectable()
export class AssetWalkFiltersState implements AssetFiltersStore {
  private readonly _assetsApi = inject(AssetsApiService);
  private readonly _spaceFacade = inject(SpaceFacade);
  private readonly _apartmentId = toSignal(this._spaceFacade.selectedApartmentId$);

  private readonly _state = signalState<AssetWalkFiltersModel>(INITIAL_STATE);
  private readonly _roomOptions = computed(() => toRoomOptions(this._state.filteringOptions()));
  private readonly _categoryOptions = computed(() => toCategoryOptions(this._state.filteringOptions()));

  /** Reloads the options narrowed to the applied selection, as the assets page does on reload. */
  private readonly _reloadFilteringOptions = rxMethod<void>(
    pipe(
      switchMap(() =>
        this._loadFilteringOptions().pipe(tap((options) => patchState(this._state, { filteringOptions: options })))
      )
    )
  );

  /** The walk always shows a single space, so the dialog hides its Space section. */
  readonly spaceScoped: Signal<boolean> = computed(() => true);
  readonly selectedSpaces: Signal<string[]> = computed(() => []);
  readonly filteringOptions = this._state.filteringOptions;
  readonly selectedRooms = this._state.selectedRooms;
  readonly selectedCategories = this._state.selectedCategories;
  readonly selectedCreators = this._state.selectedCreators;
  readonly selectedLastEdit = this._state.selectedLastEdit;

  readonly activeFilterCount = computed(
    () =>
      this._state.selectedRooms().length +
      this._state.selectedCategories().length +
      this._state.selectedCreators().length +
      (this._state.selectedLastEdit() !== undefined ? 1 : 0)
  );

  private readonly _selection = computed<FilterSelection>(() => ({
    spaces: [],
    rooms: this._state.selectedRooms(),
    categories: this._state.selectedCategories(),
    creators: this._state.selectedCreators(),
    lastEdit: this._state.selectedLastEdit()
  }));

  /**
   * Predicate for the walk. Assets proposed by auto-detection and not confirmed yet always pass:
   * the user must see what the AI suggests, whatever filters are applied.
   */
  readonly assetFilter = computed<(asset: TAsset) => boolean>(() => {
    const matchesSelection = toAssetFilterPredicate(this._selection(), this._roomOptions(), this._categoryOptions());
    return (asset: TAsset) => isAutoDetectPreviewAsset(asset.id) || matchesSelection(asset);
  });
  readonly assetFilter$ = toObservable(this.assetFilter);

  constructor() {
    // The options are scoped to the walk's apartment — load them once it is known.
    this._spaceFacade.selectedApartmentId$
      .pipe(
        filter((apartmentId): apartmentId is string => !!apartmentId),
        distinctUntilChanged(),
        takeUntilDestroyed()
      )
      .subscribe(() => this._reloadFilteringOptions());
  }

  applyFilters(selection: FilterSelection): void {
    patchState(this._state, {
      selectedRooms: selection.rooms,
      selectedCategories: selection.categories,
      selectedCreators: selection.creators,
      selectedLastEdit: selection.lastEdit
    });
    this._reloadFilteringOptions();
  }

  private _loadFilteringOptions(): Observable<AssetListFiltersOptions> {
    const apartmentId = this._apartmentId();
    return this._assetsApi
      .getFilteringOptions(
        toAssetListFilters(this._selection(), {
          apartmentIds: apartmentId ? [apartmentId] : [],
          roomOptions: this._roomOptions(),
          categoryOptions: this._categoryOptions()
        })
      )
      .pipe(catchError(() => of(EMPTY_FILTERING_OPTIONS)));
  }

  clearAllFilters(): void {
    patchState(this._state, {
      selectedRooms: [],
      selectedCategories: [],
      selectedCreators: [],
      selectedLastEdit: undefined
    });
  }
}
